import React, { JSX, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useField } from 'formik'
import { Box, FormHelperText, StandardTextFieldProps, TextField } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { isString } from 'lodash'

interface Props extends StandardTextFieldProps {
   name: string
   label: string
   fullWidth?: boolean
   searcher?: boolean
   onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void
   inputDisabled?: boolean
   multiple?: boolean
   customFields?: ReactNode[]
   noMinWidth?: boolean
   endAdornment?: JSX.Element
   inputVariant?: 'standard' | 'outlined' | 'filled'
   options: OptionType[]
}

function SelectField({
   name,
   label,
   searcher,
   options,
   inputVariant,
   onBlur,
   customFields,
   inputDisabled,
   fullWidth,
   noMinWidth,
   multiple,
   endAdornment,
}: Props) {
   const { t } = useTranslation(['TRANSLATION', 'SETTINGS'])
   const [searchPhrase, setSearchPhrase] = useState('')
   const [fieldProps, meta] = useField(name)

   return (
      <FormControl error={!!meta.error} component="fieldset" fullWidth={fullWidth} variant={inputVariant || 'standard'}>
         <InputLabel>{label}</InputLabel>
         <Select
            onBlur={(e) => {
               if (onBlur) {
                  onBlur(e)
               }
            }}
            label={label}
            endAdornment={endAdornment}
            error={Boolean(meta.error) && Boolean(meta.touched)}
            value={multiple ? (fieldProps.value ?? []) : (fieldProps.value ?? '')}
            multiple={multiple}
            onClose={() => setSearchPhrase('')}
            disabled={inputDisabled}
            MenuProps={{
               PaperProps: {
                  style: { maxHeight: '350px' },
               },
            }}
            sx={{
               minWidth: noMinWidth ? 'auto' : '260px',
               '.MuiOutlinedInput-input.MuiSelect-select': {
                  whiteSpace: 'break-spaces',
               },
            }}
            onChange={fieldProps.onChange}
            name={name}
         >
            {searcher && (
               <Box
                  sx={{
                     px: '5px',
                  }}
                  onClick={(e) => {
                     e.preventDefault()
                     e.stopPropagation()
                  }}
               >
                  <TextField
                     variant="standard"
                     sx={{ width: '100%' }}
                     defaultValue={searchPhrase}
                     placeholder={t('searchPlaceholder')}
                     onKeyDown={(e) => e.stopPropagation()}
                     onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                     }}
                     onFocus={(e) => e.target.select()}
                     onChange={(e) => {
                        setSearchPhrase(e.target.value)
                     }}
                  />
               </Box>
            )}
            {options
               .filter(
                  (item) =>
                     (isString(item.name) && item.name.toLowerCase().includes(searchPhrase.toLowerCase())) ||
                     item.value === ''
               )
               .map((option) => {
                  const isSelected = multiple
                     ? fieldProps.value?.includes(option.value)
                     : fieldProps.value === option.value

                  return (
                     <MenuItem
                        defaultValue={searchPhrase}
                        onKeyDown={(e) => e.stopPropagation()}
                        sx={{
                           height: 35,
                           whiteSpace: 'text-wrap',
                           backgroundColor: isSelected ? 'rgba(33, 147, 224, 1)' : 'transparent',
                           fontWeight: isSelected ? 'bold' : 'normal', // 🔹 pogrubienie
                        }}
                        key={option.value}
                        value={option.value}
                     >
                        {option.name}
                     </MenuItem>
                  )
               })}
            {customFields}
         </Select>
         <FormHelperText sx={{ color: 'red' }}>{meta.error && t(meta.error)}</FormHelperText>
      </FormControl>
   )
}
export default SelectField
